import React, { ComponentProps, ReactElement } from 'react';
import SentryFallback from './SentryFallback';
import { Route } from 'react-router-dom';
import { useHistory, useLocation, useRouteMatch } from 'react-router';
import * as Sentry from '@sentry/react';

const RenderComponent = ({ render }: { render: (props: any) => ReactElement }) => {
  const history = useHistory();
  const location = useLocation();
  const match = useRouteMatch();

  return render({ history, location, match }) ?? null;
};

const SentryComponent = ({ component, render }: Pick<ComponentProps<typeof Route>, 'component' | 'render'>) => {
  //Maintain availability for older components - The inner component should typically access via the hooks.
  const history = useHistory();
  const location = useLocation();
  const match = useRouteMatch();

  if (component) {
    const Component = component;
    return (
      <Sentry.ErrorBoundary fallback={SentryFallback}>
        <Component history={history} location={location} match={match} />
      </Sentry.ErrorBoundary>
    );
  } else if (render) {
    //TODO - Migrate all our 'render' uses to be proper function components and pass via 'component' instead of 'render' - They all use the same signature anyway.
    return (
      <Sentry.ErrorBoundary fallback={SentryFallback}>
        <RenderComponent render={render as (props: any) => ReactElement} />
      </Sentry.ErrorBoundary>
    );
  }
  throw new Error('A component or render function must be provided for this component');
};

/**
 * Wrapper around 'Route' to attach a Sentry error boundary around each individual Route in P4, so when we catch an error,
 * you can still see and interact with the menu's/nav bar in the usual manner.
 */
const SentryRoute = ({ component, render, children, ...routeProps }: ComponentProps<typeof Route>) => {
  if (component || render) {
    return (
      <Route {...routeProps}>
        <SentryComponent component={component} render={render} />
      </Route>
    );
  }
  return <Route {...routeProps} />;
};

export default SentryRoute;
